// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle2, XCircle, Clock, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import MentionAthletesModal from './MentionAthletesModal';

interface VendorMentionStats {
  vendor_id: string;
  vendor_name: string;
  vendor_email: string;
  total_mentions: number;
  approved_mentions: number;
  rejected_mentions: number;
  pending_mentions: number;
  pending_athlete_requests: number;
}

const MentionsTab: React.FC = () => {
  const [stats, setStats] = useState<VendorMentionStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<{ id: string; name: string } | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'approved' | 'rejected' | 'pending' | 'pending_vendor_approval'>('all');

  useEffect(() => {
    fetchVendorMentionStats();
  }, []);

  const fetchVendorMentionStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all press vendors
      const { data: vendors, error: vendorsError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .eq('role', 'vendor_press');

      if (vendorsError) throw vendorsError;
      if (!vendors) {
        setStats([]);
        return;
      }

      // For each vendor, get their mention statistics
      const statsPromises = vendors.map(async (vendor) => {
        const { data: mentions, error: mentionsError } = await supabase
          .from('profile_mentions')
          .select('status, mentioned_by')
          .eq('mentioned_by', vendor.id)
          .eq('source_type', 'press_article');

        if (mentionsError) {
          console.error('Error fetching mentions for vendor:', vendor.id, mentionsError);
          return null;
        }

        const total = mentions?.length || 0;
        const approved = mentions?.filter(m => m.status === 'approved').length || 0;
        const rejected = mentions?.filter(m => m.status === 'rejected').length || 0;
        const pending = mentions?.filter(m => m.status === 'pending').length || 0;
        const athleteRequests = mentions?.filter(m => m.status === 'pending_vendor_approval').length || 0;

        return {
          vendor_id: vendor.id,
          vendor_name: vendor.name || 'Unknown',
          vendor_email: vendor.email || '',
          total_mentions: total,
          approved_mentions: approved,
          rejected_mentions: rejected,
          pending_mentions: pending,
          pending_athlete_requests: athleteRequests,
        };
      });

      const resolvedStats = await Promise.all(statsPromises);
      const filteredStats = resolvedStats.filter((s): s is VendorMentionStats => s !== null);
      
      // Sort by total mentions descending
      filteredStats.sort((a, b) => b.total_mentions - a.total_mentions);
      
      setStats(filteredStats);
    } catch (err) {
      console.error('Error fetching vendor mention stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const calculateApprovalRate = (approved: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((approved / total) * 100);
  };

  const handleBadgeClick = (vendorId: string, vendorName: string, status: 'all' | 'approved' | 'rejected' | 'pending' | 'pending_vendor_approval') => {
    setSelectedVendor({ id: vendorId, name: vendorName });
    setSelectedStatus(status);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const totalMentions = stats.reduce((sum, s) => sum + s.total_mentions, 0);
  const totalApproved = stats.reduce((sum, s) => sum + s.approved_mentions, 0);
  const totalPending = stats.reduce((sum, s) => sum + s.pending_mentions, 0);
  const totalAthleteRequests = stats.reduce((sum, s) => sum + s.pending_athlete_requests, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mentions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMentions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalApproved}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{totalPending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Athlete Requests</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{totalAthleteRequests}</div>
          </CardContent>
        </Card>
      </div>

      {/* Vendor Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle>Press Vendor Mention Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No vendor mention data available</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Approved</TableHead>
                  <TableHead className="text-center">Rejected</TableHead>
                  <TableHead className="text-center">Pending</TableHead>
                  <TableHead className="text-center">Athlete Requests</TableHead>
                  <TableHead className="text-center">Approval Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.map((vendor) => {
                  const approvalRate = calculateApprovalRate(vendor.approved_mentions, vendor.total_mentions);
                  
                  return (
                    <TableRow key={vendor.vendor_id}>
                      <TableCell className="font-medium">{vendor.vendor_name}</TableCell>
                      <TableCell className="text-muted-foreground">{vendor.vendor_email}</TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleBadgeClick(vendor.vendor_id, vendor.vendor_name, 'all')}
                        >
                          {vendor.total_mentions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="bg-green-50 text-green-700 border-green-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleBadgeClick(vendor.vendor_id, vendor.vendor_name, 'approved')}
                        >
                          {vendor.approved_mentions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="bg-red-50 text-red-700 border-red-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleBadgeClick(vendor.vendor_id, vendor.vendor_name, 'rejected')}
                        >
                          {vendor.rejected_mentions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="bg-yellow-50 text-yellow-700 border-yellow-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleBadgeClick(vendor.vendor_id, vendor.vendor_name, 'pending')}
                        >
                          {vendor.pending_mentions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleBadgeClick(vendor.vendor_id, vendor.vendor_name, 'pending_vendor_approval')}
                        >
                          {vendor.pending_athlete_requests}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline"
                          className={
                            approvalRate >= 75 ? 'bg-green-50 text-green-700 border-green-200' :
                            approvalRate >= 50 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                            'bg-red-50 text-red-700 border-red-200'
                          }
                        >
                          {approvalRate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Athletes Modal */}
      {selectedVendor && (
        <MentionAthletesModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          vendorId={selectedVendor.id}
          vendorName={selectedVendor.name}
          status={selectedStatus}
        />
      )}
    </div>
  );
};

export default MentionsTab;
